import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { backendService } from "../services/backend.service";
import type { IncentiveConfiguration, IncentiveConfigurationWrite } from "../utils/models";
import { useErrorSnackbar } from "./useErrorSnackbar";

const queryKey = (departmentId?: string) => ["incentiveConfigurations", departmentId];

export const useIncentiveConfigurations = (departmentId?: string) => {
	return useQuery<IncentiveConfiguration[]>({
		queryKey: queryKey(departmentId),
		queryFn: async () => {
			if (!departmentId) return [];
			const response = await backendService.getIncentiveConfigurations(departmentId);
			return response.data;
		},
		enabled: !!departmentId,
	});
};

export const useCreateIncentiveConfiguration = (departmentId: string) => {
	const queryClient = useQueryClient();
	const { track } = useAnalytics();
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async (body: IncentiveConfigurationWrite) => {
			track("incentive_configuration_create_start", { department_id: departmentId });
			const response = await backendService.createIncentiveConfiguration(body);
			return response.data;
		},
		onSuccess: (data) => {
			track("incentive_configuration_create_success", { department_id: departmentId, plan_id: data.id });
			queryClient.invalidateQueries({ queryKey: queryKey(departmentId) });
		},
		onError: (error) => {
			track("incentive_configuration_create_error", { department_id: departmentId });
			showErrorSnackbar(t("incentive.configuration.save.failed"), { department_id: departmentId }, error);
		},
	});
};

export const useUpdateIncentiveConfiguration = (departmentId: string) => {
	const queryClient = useQueryClient();
	const { track } = useAnalytics();
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async ({ id, body }: { id: string; body: IncentiveConfigurationWrite }) => {
			track("incentive_configuration_update_start", { department_id: departmentId, plan_id: id });
			const response = await backendService.updateIncentiveConfiguration(id, body);
			return response.data;
		},
		onSuccess: (data) => {
			track("incentive_configuration_update_success", { department_id: departmentId, plan_id: data.id });
			queryClient.invalidateQueries({ queryKey: queryKey(departmentId) });
		},
		onError: (error, variables) => {
			track("incentive_configuration_update_error", { department_id: departmentId, plan_id: variables.id });
			showErrorSnackbar(t("incentive.configuration.save.failed"), { department_id: departmentId, plan_id: variables.id }, error);
		},
	});
};

export const useDeleteIncentiveConfiguration = (departmentId: string) => {
	const queryClient = useQueryClient();
	const { track } = useAnalytics();
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async (id: string) => {
			track("incentive_configuration_delete_start", { department_id: departmentId, plan_id: id });
			await backendService.deleteIncentiveConfiguration(id);
		},
		onSuccess: (_data, id) => {
			track("incentive_configuration_delete_success", { department_id: departmentId, plan_id: id });
			queryClient.invalidateQueries({ queryKey: queryKey(departmentId) });
		},
		onError: (error, id) => {
			track("incentive_configuration_delete_error", { department_id: departmentId, plan_id: id });
			showErrorSnackbar(t("incentive.configuration.delete.failed"), { department_id: departmentId, plan_id: id }, error);
		},
	});
};
